import gsap from "gsap/gsap-core";
import React, { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import styled from "styled-components";
import useProgress from "../../hooks/useProgress";
import useSocialShare from "../../hooks/useSocialShare";
import { Container, ContentWrapper, devices } from "../../styles";
import ShareButtons from "../Sharebuttons";
import { dictionary } from "../../contexts/site";
import { swapName } from "../../utils";

export default function Results({
  setActive,
  name,
  setName,
  answers = [],
  questions = [],
  resetProgress,
  setIsIdle
}) {
  const { score, max } = useProgress(answers, questions);
  const [animationPlayed, setAnimationPlayed] = useState(false);
  const [message, setMessage] = useState("");

  const socialShare = useSocialShare({
    url: window.location.href,
    text: dictionary.resultsPage.beatScore,
  });

  useEffect(() => {
    setActive("results");
  }, [setActive]);

  useEffect(() => {
    setName(name);
  }, [setName, name]);

  useEffect(() => {
    if (animationPlayed) {
      gsap.fromTo(".fadeIn", { autoAlpha: 0 }, { autoAlpha: 1, stagger: 0.1 });
    }
  }, [animationPlayed]);

  useEffect(() => {
    if (score <= 3) {
      // You did not successfully help ${name} exit the hospital.
      setMessage(swapName(dictionary.resultsPage.failure, name));
    } else if (score <= 6) {
      // You were close but didn’t quite manage to help ${name} exit the hospital quickly enough.
      setMessage(swapName(dictionary.resultsPage.close, name));
    } else if (score <= 9) {
      // You helped ${name} exit the hospital but it could have been faster!
      setMessage(swapName(dictionary.resultsPage.faster, name));
    } else {
      // You successfully helped ${name} exit the hospital and get back home. Well done!
      setMessage(swapName(dictionary.resultsPage.success, name));
    }
  }, [score, name]);

  useEffect(() => {

    setIsIdle(false);
    
    setTimeout(() => {
      if (score <= 6) {
        const video = document.getElementById("video");

        if (video) {
          //video.setAttribute("src", `./videos/${name}_Stage_Final.mp4`);
          video.setAttribute("src", `./videos/${name}_stage_final_fail.mp4`);
          video.style.zIndex = "10";
          // change the intro animation when the user selects a different patient
          video.pause();
          video.currentTime = 0;
          video.play();
          video.addEventListener("ended", () => {
            setAnimationPlayed(true);
          });
        }
      } else {
        setAnimationPlayed(true);
      }
    }, 100);

    setTimeout(() => {
      if (score >= 7) {
        const video = document.getElementById("video");

        if (video) {
          video.setAttribute("src", `./videos/${name}_stage_final_success.mp4`);
          video.style.zIndex = "10";
          // change the intro animation when the user selects a different patient
          video.pause();
          video.currentTime = 0;
          video.play();
          video.addEventListener("ended", () => {
            setAnimationPlayed(true);
          });
        }
      } else {
        setAnimationPlayed(true);
      }
    }, 100);
  }, [name, score, setIsIdle]);

  return (
    animationPlayed && (
      <Container className="fadeIn">
        <ResultsWrapper>
          <ScoreCard
            className="fadeIn"
            dangerouslySetInnerHTML={{__html: dictionary.resultsPage.youScored + '<br />' + score + '/' + max}}
            aria-label={dictionary.resultsPage.youScored +' '+ score +' '+ dictionary.resultsPage.outOf +' '+ max} 
            />
          <hr />
          <p className="fadeIn">{message}</p>
          <Small className="fadeIn" dangerouslySetInnerHTML={{__html: dictionary.resultsPage.scale}} />
          <hr />
          <Share className="fadeIn">
            <p dangerouslySetInnerHTML={{__html: dictionary.resultsPage.share}} />
            <ShareButtons {...socialShare} name={name}></ShareButtons>
          </Share>
          <div>
            {score !== max && (
              <>
                <hr /><h2 className="fadeIn" dangerouslySetInnerHTML={{__html: dictionary.resultsPage.improveScore}} />
              </>
            )}
            <LinkContainer>
              <Block2
                className="fadeIn"
                onClick={() => {
                  // analytics event for clicking sign up button
                  const tagManagerArgs = {
                    dataLayer: {
                      event: "gaQuizEvent",
                      eventAction: `${name}`,
                      eventLabel: `Quiz Complete - Sign Up`,
                    },
                  };
                  TagManager.dataLayer(tagManagerArgs);
                  window.location.href = "https://pbmirondeficiency.com/optin-form";
                }}
              >
                <span dangerouslySetInnerHTML={{__html: dictionary.buttons.signUp}} />
                <img alt="Envelope" src={"./signup_icon.svg"} />
              </Block2>
              <Block2
                className="fadeIn"
                onClick={() => {
                  // analytics event for clicking try again button
                  const tagManagerArgs = {
                    dataLayer: {
                      event: "gaQuizEvent",
                      eventAction: `${name}`,
                      eventLabel: `Quiz Complete - Try Again`,
                    },
                  };
                  TagManager.dataLayer(tagManagerArgs);
                  resetProgress();
                }}
              >
                <span dangerouslySetInnerHTML={{__html: dictionary.buttons.startAgain}} />
                <img alt="Curved arrow" src="./restart_icon2.svg" />
              </Block2>
              <Block
                className="fadeIn"
                onClick={() => {
                  // analytics event for clicking go to learning hub button
                  const tagManagerArgs = {
                    dataLayer: {
                      event: "gaQuizEvent",
                      eventAction: `${name}`,
                      eventLabel: `Quiz Complete - Go To Our SHPT Learning Hub`,
                    },
                  };
                  TagManager.dataLayer(tagManagerArgs);
                  window.location.href = dictionary.buttons.watchSeminarsLink}}>
                <span dangerouslySetInnerHTML={{__html: dictionary.buttons.watchSeminars}} />
                <img alt="Book" src="./webinar_icon.svg" />
              </Block>
            </LinkContainer>
          </div>
        </ResultsWrapper>
      </Container>
    )
  );
}

const ResultsWrapper = styled(ContentWrapper)`
  text-align: center;
  //margin-top: 100px;

  @media ${devices.L} {
    position: absolute;
    margin-top: 73px;
  }

  h1 {
    color: ${({ theme }) => theme.primary};
    display: inline-block;
    font-size: 22px;
    font-weight: 800;
    line-height: 28px;
    letter-spacing: 0px;
    text-align: center;
    text-transform: uppercase;

    @media ${devices.M} {
      font-size: 26px;
      line-height: 30px;
    }

    @media ${devices.L} {
      max-width: 442px;
    }
  }

  h2 {
    font-weight: 800;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 15px;
  }

  hr {
    width: 60px;
    height: 1px;
    background-color: #64C3E1;
    border: none;
    margin: 20px auto;
  }
`;

const ScoreCard = styled.div`
  color: ${({ theme }) => theme.secondary};
  font-style: normal;
  font-weight: 400;
  font-size: 21px;
  line-height: 140.5%;
`

const Block2 = styled.button`
  border-radius: 8px;
  box-shadow: 0 1px 3px 1px rgba(60, 64, 67, 0.15),
    0 1px 2px 0 rgba(60, 64, 67, 0.3);
  background: ${({ theme }) => theme.light};
  padding: 12px 20px;
  //color: ${({ theme }) => theme.primary};
  color: #0E3859;
  min-height: 64px;

  
  text-align: left;
  border: none;
  appearance: none;
  margin-bottom: 10px;
  font-weight: ${({ theme }) => theme.bold};
  letter-spacing: 0.6px;
  text-transform: uppercase;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15),
      0 1px 2px 0 rgba(60, 64, 67, 0.3);
  }

  &:last-child {
    width: 100%;
  }

  img {
    margin-left: 20px;
  }

  @media ${devices.M} {
    font-size: 14px;
    width: calc(50% - 5px);

    &:nth-child(odd) {
      //margin-right: 15px;
    }

    &:nth-child(3),
    &:nth-child(4) {
      margin-bottom: 0px;
    }

    img {
      margin-left: 15px;
      //transform: scale(-1, 1);
    }
  }

  @media ${devices.L} {
    font-size: 12px;
  }
`;

const Block = styled.button`
  border-radius: 8px;
  box-shadow: 0 1px 3px 1px rgba(60, 64, 67, 0.15),
    0 1px 2px 0 rgba(60, 64, 67, 0.3);
  background: ${({ theme }) => theme.light};
  padding: 22px 20px;
  //color: ${({ theme }) => theme.primary};
  color: #0E3859;

  
  text-align: left;
  border: none;
  appearance: none;
  margin-bottom: 10px;
  font-weight: ${({ theme }) => theme.bold};
  letter-spacing: 0.6px;
  text-transform: uppercase;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15),
      0 1px 2px 0 rgba(60, 64, 67, 0.3);
  }

  &:last-child {
    width: 100%;
  }

  img {
    margin-left: 20px;
  }

  @media ${devices.M} {
    font-size: 14px;
    width: calc(50% - 5px);

    &:nth-child(odd) {
      //margin-right: 15px;
    }

    &:nth-child(3),
    &:nth-child(4) {
      margin-bottom: 0px;
    }

    img {
      margin-left: 15px;
      //transform: scale(-1, 1);
    }
  }

  @media ${devices.L} {
    font-size: 12px;
  }
`;

const LinkContainer = styled.div`
  width: 100%;
  max-width: 355px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;

  @media ${devices.M} {
    flex-direction: row;
    flex-wrap: wrap;
    max-width: 100%;
    justify-content: space-between;
  }
`;

const Share = styled.div`
  margin-bottom: 18px;
  margin-top: 30px;
`;

const Small = styled.span`
  color: #3B2B2B;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 18px;
`;
